import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, TextField, Button, Typography, Alert, CircularProgress, useTheme, IconButton, Tooltip, Divider } from "@mui/material";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import { useAuth } from "../../context/AuthContext";
import { useThemeMode } from "../../context/ThemeModeContext";
import i18n from "../../i18n";
import { translateBackendMessage } from "../../utilities/translateBackendMessage";
import { API_BASE_URL } from "../../config/api";
import logoEscotta from "../../assets/ttflow-logo@2x.png";
import logoEscottaNegativo from "../../assets/ttflow-logo-negativo@3x.png";
import iconeEscotta from "../../assets/ttflow-icone@2x.png";
import iconeEscottaCyan from "../../assets/ttflow-icone-cyan@3x.png";

const FEATURES = [
  "Importação automática de XML",
  "Atribuição de pedidos de compra",
  "Integração com SEFAZ",
];

export default function LoginPage() {
  const { login }               = useAuth();
  const navigate                = useNavigate();
  const theme                   = useTheme();
  const { mode, toggleMode }    = useThemeMode();
  const isDark                  = theme.palette.mode === "dark";
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [error,    setError]    = useState("");
  const [loading,  setLoading]  = useState(false);

  const [ssoEmail,   setSsoEmail]   = useState("");
  const [ssoLoading, setSsoLoading] = useState(false);

  const isValidEmail = (value) => /\S+@\S+\.\S+/.test(value);

  // Resolve, no backend, qual config SSO (SAP ou Microsoft) atende o domínio do e-mail
  // informado — evita listar/expor todas as configs cadastradas antes do login.
  const resolveSsoConfig = async (path) => {
    if (!isValidEmail(ssoEmail)) {
      setError("Informe seu e-mail corporativo antes de continuar.");
      return null;
    }
    setSsoLoading(true);
    setError("");
    try {
      const res  = await fetch(`${API_BASE_URL}/${path}?email=${encodeURIComponent(ssoEmail)}`);
      const data = res.ok ? await res.json() : [];
      const list = Array.isArray(data) ? data : [];
      if (list.length === 0) {
        setError("Nenhuma configuração de login encontrada para esse domínio de e-mail.");
        return null;
      }
      return list[0];
    } catch {
      setError("Não foi possível verificar as opções de login para esse e-mail.");
      return null;
    } finally {
      setSsoLoading(false);
    }
  };

  const handleSapLogin = async () => {
    const company = await resolveSsoConfig("auth/sap/companies");
    if (!company) return;
    const state = btoa(JSON.stringify({ provider: "sap", configId: company.configId }));
    const params = new URLSearchParams({
      client_id: company.clientId,
      response_type: "code",
      scope: "openid email",
      redirect_uri: `${window.location.origin}/auth/callback`,
      state,
    });
    window.location.href = `${company.iasUrl}/oauth2/authorize?${params}`;
  };

  const handleMicrosoftLogin = async () => {
    const microsoftConfig = await resolveSsoConfig("auth/microsoft/config");
    if (!microsoftConfig) return;
    const state = btoa(JSON.stringify({ provider: "microsoft", configId: microsoftConfig.configId }));
    const params = new URLSearchParams({
      client_id: microsoftConfig.clientId,
      response_type: "code",
      scope: "openid email profile",
      redirect_uri: `${window.location.origin}/auth/callback`,
      response_mode: "query",
      state,
    });
    window.location.href = `${microsoftConfig.tenantUrl}/oauth2/v2.0/authorize?${params}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/inbound", { replace: true });
    } catch (err) {
      setError(translateBackendMessage(err.message, i18n.language?.startsWith("en")));
    } finally {
      setLoading(false);
    }
  };

  const logo = isDark ? logoEscottaNegativo : logoEscotta;

  const panelBackground = isDark
    ? theme.palette.background.paper
    : "linear-gradient(150deg, #dbe6f8 0%, #eef2fb 32%, #e1f3ee 68%, #d3ece3 100%)";

  const [glowA, glowB] = isDark
    ? ["rgba(45,212,191,0.14)", "rgba(45,212,191,0.10)"]
    : ["rgba(26,59,142,0.14)", "rgba(13,148,136,0.16)"];

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", bgcolor: "background.default" }}>

      {/* Header */}
      <Box
        sx={{
          height: 56,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
          zIndex: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <img src={isDark ? iconeEscottaCyan : iconeEscotta} alt="Escotta" style={{ width: 34, height: 34, objectFit: "contain" }} />
          <Typography sx={{ fontSize: 17, fontWeight: 700, color: "text.primary" }}>
            Inbound Automation
          </Typography>
        </Box>

        <Tooltip title={mode === "dark" ? "Modo claro" : "Modo escuro"}>
          <IconButton size="small" onClick={toggleMode} sx={{ color: "secondary.main" }}>
            {mode === "dark" ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Corpo dividido */}
      <Box sx={{ flex: 1, display: "flex" }}>

        {/* Lado esquerdo — painel decorativo */}
        <Box
          sx={{
            flex: 1,
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
            justifyContent: "center",
            px: 8,
            background: panelBackground,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box sx={{ position: "absolute", width: 520, height: 520, borderRadius: "50%", background: `radial-gradient(circle, ${glowA}, transparent 70%)`, top: -200, left: -180 }} />
          <Box sx={{ position: "absolute", width: 420, height: 420, borderRadius: "50%", background: `radial-gradient(circle, ${glowB}, transparent 70%)`, bottom: -160, right: -140 }} />

          <Box sx={{ position: "relative", zIndex: 1 }}>
            <img src={logo} alt="TTflow" style={{ height: 88, width: "auto", objectFit: "contain", marginBottom: 36, display: "block" }} />

            <Typography variant="h4" sx={{ fontWeight: 800, color: "text.primary", letterSpacing: "-0.02em", mb: 2, lineHeight: 1.2 }}>
              Gestão avançada de documentos fiscais
            </Typography>

            <Typography variant="body1" sx={{ color: "text.secondary", lineHeight: 1.7, mb: 4, maxWidth: 420 }}>
              Automatize o recebimento, validação e atribuição de NF‑e e CT‑e com integração direta ao SAP.
            </Typography>

            {FEATURES.map((feat) => (
              <Box key={feat} sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                <Box
                  sx={{
                    width: 20, height: 20, borderRadius: "6px", bgcolor: "secondary.main",
                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                  }}
                >
                  <Box component="span" sx={{ color: "secondary.contrastText", fontSize: 12, lineHeight: 1, fontWeight: 700 }}>✓</Box>
                </Box>
                <Typography sx={{ color: "text.secondary", fontSize: "0.875rem" }}>{feat}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Lado direito — formulário */}
        <Box
          sx={{
            width: { xs: "100%", md: 480 },
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            px: { xs: 3, md: 6 },
            bgcolor: "background.subtle",
          }}
        >
          <Box sx={{ width: "100%", maxWidth: 360 }}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: "text.primary", letterSpacing: "-0.02em", mb: 0.5 }}>
              Entrar
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
              Acesse com suas credenciais corporativas
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2.5, borderRadius: "8px", fontSize: "0.8rem" }}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 3 }}>
              <TextField
                label="Seu e-mail corporativo"
                type="email"
                value={ssoEmail}
                onChange={(e) => setSsoEmail(e.target.value)}
                placeholder="nome@suaempresa.com"
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: "8px", bgcolor: "background.paper" },
                }}
              />

              <Button
                variant="outlined"
                onClick={handleSapLogin}
                disabled={ssoLoading}
                startIcon={
                  <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <rect width="24" height="24" rx="3" fill="#0FAAFF" />
                    <text x="12" y="16" textAnchor="middle" fontSize="9" fontWeight="700" fill="#fff" fontFamily="Arial, sans-serif">SAP</text>
                  </svg>
                }
                sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px", fontSize: "0.875rem", py: 1.1 }}
              >
                Entrar com SAP
              </Button>

              <Button
                variant="outlined"
                onClick={handleMicrosoftLogin}
                disabled={ssoLoading}
                startIcon={
                  <svg width="16" height="16" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
                    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                  </svg>
                }
                sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px", fontSize: "0.875rem", py: 1.1 }}
              >
                Entrar com Microsoft
              </Button>

              <Divider sx={{ my: 0.5 }}>
                <Typography sx={{ fontSize: "0.7rem", color: "text.secondary" }}>ou</Typography>
              </Divider>
            </Box>

            <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="E-mail"
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: "8px", bgcolor: "background.paper" },
                  "& .MuiOutlinedInput-input": { padding: "14.5px 14px !important" },
                }}
              />
              <TextField
                label="Senha"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: "8px", bgcolor: "background.paper" },
                  "& .MuiOutlinedInput-input": { padding: "14.5px 14px !important" },
                }}
              />
              <Button
                type="submit"
                variant="contained"
                disableElevation
                disabled={loading}
                startIcon={loading ? <CircularProgress size={14} color="inherit" /> : null}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: "8px",
                  py: 1.1,
                  mt: 0.5,
                  fontSize: "0.875rem",
                }}
              >
                {loading ? "Entrando..." : "Entrar"}
              </Button>
            </Box>

            <Typography variant="caption" sx={{ color: "text.secondary", display: "block", textAlign: "center", mt: 4 }}>
              © {new Date().getFullYear()} Escotta Consultoria
            </Typography>
          </Box>
        </Box>

      </Box>
    </Box>
  );
}
